import { useQuery } from "@tanstack/react-query";

// Public plan catalogue and usage fees (GET /api/public/plans). Used by the
// landing page and the pricing docs. Fetched without the API client so it
// never sends credentials. When the request fails, the static defaults below
// are returned so marketing pages always render.

export interface PublicPlanFeatures {
  byokFeePercent: number;
  appFeeDiscountPercent: number;
  maxClients: number;
}

export interface PublicPlan {
  name: string;
  displayName: string;
  monthlyPrice: number;
  features: PublicPlanFeatures;
  sortOrder: number;
}

export interface PublicFees {
  platformMarkupPercent: number;
  byokFeePercentDefault: number;
  cacheHitMode: string;
  cacheHitPricePercent: number;
  ragQueryFee: number;
  agentRunFee: number;
  workflowRunFee: number;
  topUpFeePercent: number;
  topUpFeeFixed: number;
  topUpMin: number;
  platformKeysEnabled: boolean;
}

export interface PublicPlans {
  plans: PublicPlan[];
  fees: PublicFees;
  /** true when the values come from the static fallback, not the server. */
  isFallback: boolean;
}

export const PUBLIC_PLANS_KEY = ["public-plans"] as const;

export const FALLBACK_PLANS: PublicPlan[] = [
  { name: "payg", displayName: "Developer", monthlyPrice: 0, features: { byokFeePercent: 5, appFeeDiscountPercent: 0, maxClients: 0 }, sortOrder: 0 },
  { name: "pro", displayName: "Pro", monthlyPrice: 29, features: { byokFeePercent: 3, appFeeDiscountPercent: 25, maxClients: 0 }, sortOrder: 1 },
  { name: "agency", displayName: "Agency", monthlyPrice: 199, features: { byokFeePercent: 2, appFeeDiscountPercent: 50, maxClients: 10 }, sortOrder: 2 },
  { name: "agency_scale", displayName: "Agency Scale", monthlyPrice: 499, features: { byokFeePercent: 1, appFeeDiscountPercent: 50, maxClients: 50 }, sortOrder: 3 },
];

export const FALLBACK_FEES: PublicFees = {
  platformMarkupPercent: 5,
  byokFeePercentDefault: 5,
  cacheHitMode: "discounted",
  cacheHitPricePercent: 50,
  ragQueryFee: 0.002,
  agentRunFee: 0.01,
  workflowRunFee: 0.01,
  topUpFeePercent: 5,
  topUpFeeFixed: 0.35,
  topUpMin: 10,
  platformKeysEnabled: true,
};

export const FALLBACK_PUBLIC_PLANS: PublicPlans = { plans: FALLBACK_PLANS, fees: FALLBACK_FEES, isFallback: true };

const num = (v: unknown, fallback: number) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : fallback;
};

export function parsePlan(raw: unknown, index: number): PublicPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.name !== "string" || !p.name) return null;
  const known = FALLBACK_PLANS.find((f) => f.name === p.name);
  const f = (p.features && typeof p.features === "object" ? p.features : {}) as Record<string, unknown>;
  return {
    name: p.name,
    displayName: typeof p.displayName === "string" && p.displayName ? p.displayName : known?.displayName ?? p.name,
    monthlyPrice: num(p.monthlyPrice, known?.monthlyPrice ?? 0),
    features: {
      byokFeePercent: num(f.byokFeePercent, known?.features.byokFeePercent ?? FALLBACK_FEES.byokFeePercentDefault),
      appFeeDiscountPercent: num(f.appFeeDiscountPercent, known?.features.appFeeDiscountPercent ?? 0),
      maxClients: num(f.maxClients, known?.features.maxClients ?? 0),
    },
    sortOrder: num(p.sortOrder, index),
  };
}

export function parsePublicFees(raw: unknown): PublicFees {
  const d = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const F = FALLBACK_FEES;
  return {
    platformMarkupPercent: num(d.platformMarkupPercent, F.platformMarkupPercent),
    byokFeePercentDefault: num(d.byokFeePercentDefault, F.byokFeePercentDefault),
    cacheHitMode: typeof d.cacheHitMode === "string" ? d.cacheHitMode : F.cacheHitMode,
    cacheHitPricePercent: num(d.cacheHitPricePercent, F.cacheHitPricePercent),
    ragQueryFee: num(d.ragQueryFee, F.ragQueryFee),
    agentRunFee: num(d.agentRunFee, F.agentRunFee),
    workflowRunFee: num(d.workflowRunFee, F.workflowRunFee),
    topUpFeePercent: num(d.topUpFeePercent, F.topUpFeePercent),
    topUpFeeFixed: num(d.topUpFeeFixed, F.topUpFeeFixed),
    topUpMin: num(d.topUpMin, F.topUpMin),
    platformKeysEnabled: typeof d.platformKeysEnabled === "boolean" ? d.platformKeysEnabled : F.platformKeysEnabled,
  };
}

/** Effective price of a cache hit as a % of the normal price ("free" → 0, "full" → 100). */
export function cacheHitPercent(fees: PublicFees): number {
  if (fees.cacheHitMode === "free") return 0;
  if (fees.cacheHitMode === "full") return 100;
  return fees.cacheHitPricePercent;
}

/** Money with up to 3 decimals ($0.002, $0.01, $29). */
export function formatFee(amount: number): string {
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 })}`;
}

export function usePublicPlans() {
  const query = useQuery({
    queryKey: PUBLIC_PLANS_KEY,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<PublicPlans> => {
      const res = await fetch("/api/public/plans", { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const d = body?.data ?? {};
      const plans = (Array.isArray(d.plans) ? d.plans : [])
        .map(parsePlan)
        .filter((p: PublicPlan | null): p is PublicPlan => p !== null)
        .sort((a: PublicPlan, b: PublicPlan) => a.sortOrder - b.sortOrder);
      return {
        plans: plans.length ? plans : FALLBACK_PLANS,
        fees: parsePublicFees(d.fees),
        isFallback: false,
      };
    },
  });
  return { ...query, plans: query.data ?? FALLBACK_PUBLIC_PLANS };
}
